// ═══════════════════════════════════════════════════════════════
// GUIDES — long-form reading content shown in GuideReaderModal
// Path: src/constants/guides.js
//
// Structured data (not markdown) so the reader component can style
// each block without pulling in a markdown parser. Source-of-truth
// narrative also lives in src/docs/ as a .md file per guide.
// ═══════════════════════════════════════════════════════════════

// Block types:
//   { type: "heading", level: 1|2|3, text }
//   { type: "paragraph", text }
//   { type: "list", items: [string] }
//   { type: "table", headers: [string], rows: [[string, string, ...]] }
//   { type: "callout", variant: "info"|"warn", text }
//   { type: "divider" }

export const GUIDES = {
  capacity_for_small_shops: {
    id: "capacity_for_small_shops",
    title: "Capacity View for Small Shops",
    subtitle:
      "How to do real prospect research without a wealth-screening subscription",
    meta: "A practical guide for small development teams · April 2026",
    readingTime: "7 min read",
    blocks: [
      { type: "heading", level: 2, text: "Who this is for" },
      {
        type: "paragraph",
        text: "You are a development director, executive director, or volunteer fundraiser at a small nonprofit or chapter. Your budget won't stretch to a WealthEngine subscription. iWave and DonorSearch are on the \"someday\" list. Meanwhile, you are expected to build a major-gift pipeline, rate prospects, and defend your portfolio strategy to the board.",
      },
      {
        type: "paragraph",
        text: "This guide explains how ClearView Philanthropy CRM's Capacity View is deliberately designed to do most of what a paid wealth-screening tool does — at zero incremental cost — and exactly where the commercial tools still pull ahead so you know when the investment is worth making.",
      },

      { type: "heading", level: 2, text: "The reality of wealth screening in 2026" },
      {
        type: "paragraph",
        text: "Commercial wealth-screening tools price out most shops under $5M in annual revenue:",
      },
      {
        type: "table",
        headers: ["Tool", "Rough annual cost"],
        rows: [
          ["DonorSearch (entry)", "$2,500 – $6,000"],
          ["iWave", "$7,000 – $15,000"],
          ["WealthEngine", "$15,000 – $40,000"],
          ["Blackbaud Target Analytics", "$10,000 – $50,000"],
        ],
      },
      {
        type: "paragraph",
        text: "Many small shops buy into one of these tools, use it once for a big batch-screen at the start of a campaign, and then let the subscription renew unused for years. The money is real. The return rarely is.",
      },
      {
        type: "callout",
        variant: "info",
        text: "The Capacity View is designed around a different proposition: what if the CRM itself did everything a small shop actually needs from wealth screening, and made the paid tools optional rather than required?",
      },

      { type: "heading", level: 2, text: "What wealth screens actually give you" },
      {
        type: "paragraph",
        text: "Before claiming to replace one, it helps to name what they do. A typical wealth-screening report includes:",
      },
      {
        type: "list",
        items: [
          "A capacity rating — usually a dollar band (\"$10K–$25K\", \"$100K–$1M\")",
          "Real estate holdings — estimated value across all properties",
          "Public stock holdings — from SEC insider filings (Form 3/4/5) and 13D/13G",
          "Political contributions — from FEC records",
          "Foundation affiliations — officer or trustee roles at private foundations",
          "Business ownership — executive titles, corporate board seats",
          "Luxury markers — aircraft, yachts, country club memberships (sometimes)",
          "A propensity score — the vendor's secret-sauce guess at likelihood to give",
        ],
      },
      {
        type: "paragraph",
        text: "The vendors don't own most of this data. They aggregate it from public records (SEC EDGAR, FEC, county assessors, IRS 990-PF filings, LinkedIn) and sell you the aggregation. What you are paying for is convenience, not exclusive intelligence.",
      },

      { type: "heading", level: 2, text: "What the Capacity View does instead — for free" },
      {
        type: "paragraph",
        text: "The Capacity View is built around a simple observation: a donor's own giving history and engagement is the single most reliable indicator of their capacity to give more. Everything else wealth screens add is supplementary.",
      },

      { type: "heading", level: 3, text: "1. A suggested capacity band, on day one" },
      {
        type: "paragraph",
        text: "Every donor in your database gets a system-suggested 5-year giving capacity inferred from their actual giving history — using the long-standing fundraising rule that donors typically give 5–10% of real capacity annually. A donor who has given your organization $2,000 over 5 years is flagged at $10K–$50K. A donor who has given $100,000 is flagged at $1M+. The view is useful the day you turn it on.",
      },
      {
        type: "paragraph",
        text: "These suggestions are clearly marked as system inferences (the word SUGGESTED appears in italics on the row). As you confirm or override them, they become part of your institutional knowledge.",
      },

      { type: "heading", level: 3, text: "2. A triage list that surfaces the right people to call this week" },
      {
        type: "paragraph",
        text: "The ranked list answers the question fundraisers actually ask: \"Where should I spend my time?\" The default sort is Opportunity Gap — the dollar amount each donor could plausibly give beyond what they already have. A $500 annual donor whose suggested capacity is $25,000 jumps to the top: that's a relationship you should be tending.",
      },
      {
        type: "paragraph",
        text: "Filter to your own portfolio, filter by pathway stage, sort by oldest last-contact, and you have a weekly call list without any outside data.",
      },

      { type: "heading", level: 3, text: "3. A confidence score that tells you how defensible each estimate is" },
      {
        type: "paragraph",
        text: "Every donor has a 0–100% confidence score composed of six weighted signals:",
      },
      {
        type: "list",
        items: [
          "Giving history depth (25%) — count of gifts, tenure, recency",
          "Relationship depth (20%) — logged contacts, filed contact reports",
          "Director assessment (20%) — your own professional judgment",
          "External wealth data (15%) — reserved for vendor imports when available",
          "Engagement signal (10%) — the pathway engine you already use",
          "Network & role (10%) — board service, committees, persona tags",
        ],
      },
      {
        type: "paragraph",
        text: "For shops without a wealth-screening subscription, the External Wealth component silently redistributes its 15% to the other five. Your donors are not permanently capped at 85% confidence just because you didn't buy a vendor tool.",
      },

      { type: "heading", level: 3, text: "4. Gap analysis — what to do to strengthen your estimate" },
      {
        type: "paragraph",
        text: "Below each low-scoring component is a specific next move: \"Log a substantive contact — the fastest way to raise this score\" or \"Add a narrative note explaining what you know about this donor's capacity.\" The confidence breakdown becomes a to-do list.",
      },

      { type: "heading", level: 3, text: "5. A narrative field for the knowledge that lives in your head" },
      {
        type: "paragraph",
        text: "The Director's Assessment section on each donor's Capacity tab captures what a wealth screen can never know: \"Met at the gala in 2024; mentioned estate planning twice; spouse is on the county hospital board.\" This is your institutional memory. It survives staff turnover. It counts for 20% of confidence.",
      },

      { type: "heading", level: 3, text: "6. A structured home for your public-record research" },
      {
        type: "paragraph",
        text: "Every donor's Capacity tab has a \"Research this donor\" button. Click it and a form opens with the same fields a DonorSearch report would populate: real estate value, public stock value, political giving total, foundation affiliations, business or board affiliations, and notes. Enter what you find from free public sources; the data lands in the same 15% External Wealth component of the confidence score as a paid import would.",
      },
      {
        type: "paragraph",
        text: "The modal includes an inline cheat-sheet of the five free sources to consult. Dollar fields understand \"$1.2M\" and \"$1,234,567\" without cleanup. A green \"MANUAL RESEARCH\" badge marks donors where the data came from your fieldwork rather than a vendor — and the two sources can coexist seamlessly.",
      },

      { type: "heading", level: 2, text: "The free research supplements that close most remaining gaps" },
      {
        type: "paragraph",
        text: "If a donor makes your short list and you want to do the research a wealth screen would do, here are the free public sources — and in the CRM, every donor's Capacity tab now has a dedicated 'Research this donor' button where your findings land in structured fields (real estate, stock, political giving, foundations, business affiliations). Those fields count for the same 15% of the confidence score as a paid DonorSearch import would.",
      },
      {
        type: "table",
        headers: ["Wealth-screen field", "Where to find it for free"],
        rows: [
          ["Real estate value", "County assessor website (search by name); Zillow for an estimate"],
          ["Public stock holdings", "SEC EDGAR (search by name for Form 4 filings)"],
          ["Political giving", "FEC.gov (federal); OpenSecrets.org for aggregation"],
          ["Foundation affiliations", "Candid / Foundation Directory Online (free tier); IRS 990-PF on ProPublica Nonprofit Explorer"],
          ["Business ownership", "LinkedIn (public profile); OpenCorporates for board seats"],
          ["Giving to peer nonprofits", "990 filings on ProPublica Nonprofit Explorer"],
        ],
      },
      {
        type: "callout",
        variant: "info",
        text: "For a single donor, this takes 15–20 minutes. For a list of 25 priority prospects, that is a half-day of focused research — roughly the time an ED spends on one failed discovery meeting. You do not need a $10,000 tool to do this work; you need a CRM that tells you which 25 donors to focus on AND gives you a structured place to record what you find. That is the Capacity View.",
      },
      {
        type: "paragraph",
        text: "The Research modal includes an inline cheat-sheet of these five sources, so you don't have to keep this guide open while you work.",
      },

      { type: "heading", level: 2, text: "A side-by-side: what changes if you add a wealth screen later" },
      {
        type: "table",
        headers: [
          "Without a wealth-screen subscription",
          "With DonorSearch / iWave / WealthEngine",
        ],
        rows: [
          ["Suggested bands from giving × 5", "Vendor-rated bands from aggregated data"],
          ["Confidence scores up to ~92% (5 components, rebalanced)", "Confidence scores up to 100% (6 components)"],
          ["You look up real estate / stock / politics one donor at a time", "Batch-rated data across your whole file"],
          ["Director judgment carries 20% of the score", "Director judgment + external data together"],
          ["Works best for shops with < 500 major-gift prospects", "Scales cleanly past 1,000+ prospects"],
          ["Free", "$2,500 – $40,000 per year"],
        ],
      },
      {
        type: "paragraph",
        text: "When you do buy in, the Capacity View picks up the CSV import automatically — no migration, no reconfiguration. Import once in Settings → Data, and the External Wealth component activates across every donor with a match.",
      },

      { type: "heading", level: 2, text: "When a wealth screen is genuinely worth the money" },
      {
        type: "paragraph",
        text: "We are not anti-wealth-screen. The tools have real value in specific situations:",
      },
      {
        type: "list",
        items: [
          "Capital campaigns — when you need a batch-rated file of several thousand donors in a short window",
          "Prospect discovery at scale — when you need to screen against a purchased list or event registrant file of people you don't yet have relationships with",
          "Major-gift officer teams of 3+ — when you cannot afford the research time per prospect",
          "Board-mandated vendor ratings — when your board wants a third-party-verified number on every major gift prospect",
        ],
      },
      {
        type: "paragraph",
        text: "If none of those describe you right now, the Capacity View + your own judgment will carry you further than you might expect — and when the time comes to add a paid tool, the infrastructure is ready.",
      },

      { type: "heading", level: 2, text: "A practical workflow for a small shop using the Capacity View alone" },

      { type: "heading", level: 3, text: "Monday morning (20 minutes)" },
      {
        type: "list",
        items: [
          "Open the Capacity View (toolbar button or Ctrl+K → /capacity).",
          "Filter portfolio to Mine, sort by Opportunity Gap.",
          "Look at the top 10 rows. These are your week's focus.",
        ],
      },

      { type: "heading", level: 3, text: "Tuesday – Thursday (as time allows)" },
      {
        type: "list",
        items: [
          "Click into each of the 10 donors. The Capacity tab opens automatically.",
          "Read the confidence breakdown. Take the \"Next Move\" action it suggests — usually \"log a substantive contact\" or \"enter your capacity estimate.\"",
          "For 2–3 donors per week, click the \"Research this donor\" button on the Capacity tab. Spend 15 minutes on free public sources (county assessor, SEC EDGAR, FEC.gov, 990-PF filings, LinkedIn) and enter what you find into the structured fields. Each submission lifts the donor's confidence score by up to 15%.",
          "Add narrative context (the who-said-what that a web search can't give you) in the Director's Assessment narrative field.",
        ],
      },

      { type: "heading", level: 3, text: "Friday (10 minutes)" },
      {
        type: "list",
        items: [
          "Return to the Capacity View. Watch the Summary rail: Total Rated Capacity goes up as confirmed bands replace suggested ones. Average Confidence rises as you log contacts and add assessments.",
          "At quarter end, export or screenshot the view for your board report: \"We have $X in rated capacity across the portfolio, Y% confidence on average, and an opportunity gap of $Z we are working through.\"",
        ],
      },
      {
        type: "paragraph",
        text: "This workflow, done consistently for a year, produces the same board-ready major-gift strategy a wealth-screen subscription would — for people whose budgets don't permit the latter.",
      },

      { type: "heading", level: 2, text: "The deeper point" },
      {
        type: "paragraph",
        text: "Wealth screens answer the question \"How rich is this person?\" Fundraising success depends on a different question: \"How close is the relationship, and where is the next move?\"",
      },
      {
        type: "paragraph",
        text: "A good CRM that surfaces giving patterns, captures director judgment, and computes defensible confidence scores will carry a small shop most of the way. The Capacity View is that tool. When the time comes to add a wealth screen, you'll use it as a supplement to a strategy you've already built — rather than a crutch for a strategy you never had.",
      },

      { type: "divider" },
      {
        type: "paragraph",
        text: "This document is a practical guide — not a contract. Wealth-screen vendors update their offerings constantly; pricing quoted is indicative only. Check the vendors' current terms before making a purchasing decision.",
      },
    ],
  },
};

export const getGuide = (id) => GUIDES[id] || null;
